import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import { Dialog, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';
import GooglePlacesAutocomplete from 'react-google-autocomplete';
import AdminLoader from '../AdminLoader';
import { UserIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Nombre total d'utilisateurs dans la base de données
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  // Recherche et pagination
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  // Tri
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  // État de la modale de création
  const [createModal, setCreateModal] = useState({ open: false, loading: false, error: null });
  // État du formulaire de création
  const [form, setForm] = useState({
    lastname: '',
    firstname: '',
    birth_date: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipcode: '',
    country: 'France',
    host_name: '',
    parentInput: '',
    parent_id: null,
  });
  
  // Détermine si on crée un membre de famille ou un utilisateur principal
  const isCreatingFamilyMember = form.parent_id !== null;
  // Log pour déboguer
  console.log('État du formulaire:', { 
    parentInput: form.parentInput, 
    parent_id: form.parent_id, 
    isCreatingFamilyMember 
  });
  const [parentOptions, setParentOptions] = useState([]);
  const parentLabelToIdRef = useRef({});
  
  // Auth non requise ici pour restreindre l'UI; toute restriction se fait côté serveur si nécessaire.
  
  // Ancre pour remonter en haut de la liste
  const topRef = useRef(null);

  // Scroll fluide vers le haut du composant
  const scrollToTop = () => {
    if (topRef.current && typeof topRef.current.scrollIntoView === 'function') {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (typeof window !== 'undefined' && window.scrollTo) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Change de page puis remonte en haut
  const goToPage = (newPage) => {
    setPage(newPage);
    scrollToTop();
  };
  
  // isRestrictedUser supprimé: tout utilisateur connecté peut créer et modifier des contacts

  // Ouvrir/fermer la modale
  const handleOpenCreateModal = () => {
    setForm({
      lastname: '', firstname: '', birth_date: '', email: '', phone: '', address: '', city: '', zipcode: '', country: 'France', host_name: '',
      parentInput: '', parent_id: null,
    });
    setParentOptions([]);
    parentLabelToIdRef.current = {};
    setCreateModal({ open: true, loading: false, error: null });
  };
  const handleCloseCreateModal = () => {
    setCreateModal(prev => ({ ...prev, open: false }));
  };

  // MAJ du formulaire
  const updateForm = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Gère la sélection d'une adresse depuis Google Places.
   * Extrait numéro + rue, ville, code postal, pays et met à jour l'état `form`.
   * Cette logique est alignée sur `Payment.js` pour assurer la cohérence UX.
   * @param {google.maps.places.PlaceResult} place - Objet renvoyé par Google Places
   */
  const handlePlaceSelect = (place) => {
    if (!place) return;
    const addressComponents = place.address_components || [];
    // Récupération sécurisée des différents composants d'adresse
    const streetNumber = addressComponents.find(c => c.types?.includes('street_number'))?.long_name || '';
    const route = addressComponents.find(c => c.types?.includes('route'))?.long_name || '';
    const city = addressComponents.find(c => c.types?.includes('locality'))?.long_name || '';
    const zipCode = addressComponents.find(c => c.types?.includes('postal_code'))?.long_name || '';
    const country = addressComponents.find(c => c.types?.includes('country'))?.long_name || '';

    // MAJ des champs du formulaire (adresse, ville, CP, pays)
    setForm(prev => ({
      ...prev,
      address: `${streetNumber} ${route}`.trim(),
      city: city,
      zipcode: zipCode,
      country: country || prev.country || 'France',
    }));
  };

  // Recherche parent (datalist)
  const parentSearchTimeout = useRef(null);
  const handleParentInputChange = (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, parentInput: value, parent_id: null }));
    if (parentSearchTimeout.current) window.clearTimeout(parentSearchTimeout.current);
    parentSearchTimeout.current = window.setTimeout(async () => {
      await searchParents(value);
    }, 300);
  };
  const onParentInputBlur = () => {
    const id = parentLabelToIdRef.current[form.parentInput];
    // Mise à jour explicite de parent_id et log pour déboguer
    const newParentId = id ?? null;
    console.log('Parent sélectionné:', form.parentInput, 'ID:', newParentId);
    setForm(prev => ({ ...prev, parent_id: newParentId }));
  };
  const searchParents = async (text) => {
    try {
      const q = (text || '').trim();
      if (!q) {
        setParentOptions([]);
        parentLabelToIdRef.current = {};
        return;
      }
      let query = supabase
        .from('users')
        .select('id, firstname, lastname, email, phone')
        .limit(20);
      const tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
      const fields = ['firstname', 'lastname', 'email', 'phone'];
      const orParts = [];
      for (const t of tokens) for (const f of fields) orParts.push(`${f}.ilike.%${t}%`);
      if (orParts.length) query = query.or(orParts.join(','));
      const { data, error } = await query;
      if (error) throw error;
      const labels = [];
      const map = {};
      (data || []).forEach(u => {
        const label = `${u.lastname ?? ''} ${u.firstname ?? ''} — ${u.email ?? ''} — ${u.phone ?? ''}`.trim();
        labels.push({ id: u.id, label });
        map[label] = u.id;
      });
      parentLabelToIdRef.current = map;
      setParentOptions(labels);
    } catch (err) {
      console.error('Erreur de recherche parent:', err);
    }
  };

  // Validation et soumission du formulaire
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateModal(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Validation côté client
      if (!form.lastname.trim() || !form.firstname.trim()) {
        throw new Error('Veuillez remplir le nom et le prénom.');
      }

      if (isCreatingFamilyMember) {
        // Création d'un membre de famille
        const payload = {
          lastname: form.lastname.trim(),
          firstname: form.firstname.trim(),
          birth_date: form.birth_date,
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          parent_id: form.parent_id,
        };

        const { error: insertError } = await supabase
          .from('family_members')
          .insert([payload])
          .select('*')
          .single();

        if (insertError) {
          throw insertError;
        }

        toast.success('Membre de famille créé avec succès !');
      } else {
        // Création d'un utilisateur principal
        if (!form.email.trim() || !form.phone.trim() || !form.address.trim() || !form.city.trim() || !form.zipcode.trim() || !form.country.trim()) {
          throw new Error('Veuillez remplir tous les champs obligatoires.');
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
          throw new Error('Veuillez saisir un email valide.');
        }
        if (form.phone.length < 8) {
          throw new Error('Le numéro de téléphone doit contenir au moins 8 caractères.');
        }

        const payload = {
          lastname: form.lastname.trim(),
          firstname: form.firstname.trim(),
          birth_date: form.birth_date,
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          city: form.city.trim(),
          zipcode: form.zipcode.trim(),
          country: form.country.trim(),
          host_name: form.host_name.trim() || null,
        };
        const { error: insertError } = await supabase
          .from('users')
          .insert([payload])
          .select('*')
          .single();

        if (insertError) {
          if (insertError.code === '23505') {
            if (insertError.message.includes('users_email_key')) {
              throw new Error('Cet email est déjà utilisé par un autre contact.');
            } else if (insertError.message.includes('users_phone_key')) {
              throw new Error('Ce numéro de téléphone est déjà utilisé par un autre contact.');
            }
          }
          throw insertError;
        }

        toast.success('Contact créé avec succès !');
      }

      handleCloseCreateModal();
      fetchUsers();
      fetchTotalUsersCount();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      const errorMessage = error.message || 'Une erreur est survenue lors de la création.';
      setCreateModal(prev => ({ ...prev, error: errorMessage }));
      toast.error(errorMessage);
    } finally {
      setCreateModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Fonction pour récupérer le nombre total d'utilisateurs
  const fetchTotalUsersCount = async () => {
    try {
      const { count, error } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });
      
      if (error) {
        console.error("Erreur lors du comptage des utilisateurs :", error);
        setTotalUsersCount(0);
      } else {
        setTotalUsersCount(count);
      }
    } catch (err) {
      console.error("Erreur inattendue lors du comptage des utilisateurs :", err);
      setTotalUsersCount(0);
    }
  };

  // Fonction pour récupérer les utilisateurs avec filtres et tri
  /**
   * Récupère les utilisateurs.
   * @param {string|null|undefined} overrideSearch - Valeur de recherche à utiliser à la place de l'état `search` (utile après reset).
   */
  const fetchUsers = async (overrideSearch) => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('users')
        .select('*')
        .order(sortField, { ascending: sortOrder === 'asc' });
      
      // Appliquer le filtre de recherche si présent
      const activeSearch = (overrideSearch ?? search) || '';
      if (activeSearch && activeSearch.trim()) {
        // Recherche multi-mots: on découpe la requête en tokens sur les espaces
        const tokens = activeSearch.toLowerCase().trim().split(/\s+/).filter(Boolean);
        // Champs concernés par la recherche
        const fields = ['firstname', 'lastname', 'email', 'phone', 'city', 'country', 'address'];
        // Une seule clause OR agrégeant chaque combinaison champ x token
        const orParts = [];
        for (const t of tokens) {
          for (const f of fields) {
            orParts.push(`${f}.ilike.%${t}%`);
          }
        }
        if (orParts.length > 0) {
          // PostgREST n'accepte qu'une seule clause OR -> on la construit en CSV
          query = query.or(orParts.join(','));
        }
      }
      
      const { data, error: fetchError } = await query;
      
      if (fetchError) {
        setError("Erreur lors du chargement des utilisateurs : " + fetchError.message);
        setUsers([]);
      } else {
        // Enrichissement: déterminer si chaque utilisateur a au moins un membre de famille
        let enriched = Array.isArray(data) ? data : [];
        try {
          const ids = enriched.map(u => u?.id).filter(Boolean);
          const parentsWithFamily = new Set();
          if (ids.length > 0) {
            // Traiter par lots pour éviter une URL trop longue avec .in()
            const chunkSize = 100; // taille prudente
            for (let i = 0; i < ids.length; i += chunkSize) {
              const chunk = ids.slice(i, i + chunkSize);
              const { data: famRows, error: famError } = await supabase
                .from('family_members')
                .select('parent_id')
                .not('parent_id', 'is', null)
                .in('parent_id', chunk);
              if (famError) {
                console.warn('Erreur chargement family_members (batch):', famError);
                continue; // ne bloque pas l'affichage, on passe au batch suivant
              }
              if (Array.isArray(famRows)) {
                famRows.forEach(r => { if (r?.parent_id) parentsWithFamily.add(r.parent_id); });
              }
            }
          }
          enriched = enriched.map(u => ({ ...u, hasFamily: parentsWithFamily.has(u.id) }));
        } catch (e) {
          // En cas d'échec de l'enrichissement, continuer sans bloquer l'affichage
          console.warn('Enrichissement hasFamily échoué:', e);
          enriched = Array.isArray(data) ? data : [];
        }

        // Si une recherche est active, on trie localement par pertinence
        const activeSearch2 = (overrideSearch ?? search) || '';
        if (activeSearch2 && activeSearch2.trim() && Array.isArray(enriched)) {
          /**
           * Supprime les accents/diacritiques d'une chaîne pour rendre la recherche insensible aux accents.
           * Exemple: "Kouamé Affoué" -> "Kouame Affoue"
           */
          const removeAccents = (value) => {
            if (typeof value !== 'string') return value;
            return value.normalize('NFD').replace(/\p{Diacritic}+/gu, '');
          };

          // Tokens et phrase complète normalisés (sans accents)
          const tokensRaw = activeSearch2.toLowerCase().trim().split(/\s+/).filter(Boolean);
          const tokens = tokensRaw.map(removeAccents);
          const fullPhrase = removeAccents(activeSearch2.toLowerCase().trim());

          // Construit un texte de référence par enregistrement pour le scoring
          const buildHaystack = (u) => [
            u.firstname, u.lastname, u.email, u.phone, u.city, u.country, u.address
          ].filter(Boolean).join(' ').toLowerCase();
          // Normaliser le haystack sans accents
          const buildHaystackNormalized = (u) => removeAccents(buildHaystack(u));

          /**
           * Calcule un score de pertinence pour l'utilisateur donné.
           * - +100 par token présent (favorise les fiches contenant tous les mots)
           * - +5 par occurrence totale des tokens (désambiguisation fine)
           * - +10 si la phrase complète apparaît (bonus phrase exacte)
           */
          const computeSearchScore = (u, tokens, full) => {
            const text = buildHaystackNormalized(u);
            let matchedTokens = 0;
            let occurrences = 0;
            for (const t of tokens) {
              if (!t) continue;
              if (text.includes(t)) {
                matchedTokens++;
                // Compte des occurrences de chaque token
                let idx = text.indexOf(t);
                while (idx !== -1) {
                  occurrences++;
                  idx = text.indexOf(t, idx + t.length);
                }
              }
            }
            const phraseBonus = full && text.includes(full) ? 10 : 0;
            return matchedTokens * 100 + occurrences * 5 + phraseBonus;
          };

          // Tri décroissant par score, puis retombée sur le tri (champ/ordre) existant
          const sorted = [...enriched].sort((a, b) => {
            const sa = computeSearchScore(a, tokens, fullPhrase);
            const sb = computeSearchScore(b, tokens, fullPhrase);
            if (sb !== sa) return sb - sa;

            // Égalité de score: tri secondaire selon sortField/sortOrder
            const av = a?.[sortField];
            const bv = b?.[sortField];
            if (av == null && bv != null) return 1;
            if (av != null && bv == null) return -1;
            if (av == null && bv == null) return 0;

            if (typeof av === 'string' && typeof bv === 'string') {
              // Tri secondaire accent-insensible pour cohérence
              const cmp = av.localeCompare(bv, 'fr', { sensitivity: 'base' });
              return sortOrder === 'asc' ? cmp : -cmp;
            }
            // Tentative de comparaison date/numérique sinon lexicographique
            const ad = new Date(av).getTime();
            const bd = new Date(bv).getTime();
            if (!Number.isNaN(ad) && !Number.isNaN(bd)) {
              return sortOrder === 'asc' ? ad - bd : bd - ad;
            }
            if (av === bv) return 0;
            return sortOrder === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
          });

          setUsers(sorted);
        } else {
          setUsers(enriched);
        }
      }

      // Récupérer le nombre total d'utilisateurs
      await fetchTotalUsersCount();
    } catch (err) {
      setError("Erreur inattendue : " + err.message);
      setUsers([]);
    }
    setLoading(false);
  };

  // Charger les utilisateurs au chargement initial et lors des changements de tri
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder]);

  // Charger le nombre total d'utilisateurs au chargement initial
  useEffect(() => {
    fetchTotalUsersCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination des résultats
  const totalPages = Math.max(1, Math.ceil(users.length / perPage));
  const paginatedUsers = users.slice((page - 1) * perPage, page * perPage);

  // Gestion des changements dans le champ de recherche
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Gestion du nombre de résultats par page
  const handlePerPage = (e) => {
    setPerPage(Number(e.target.value));
    setPage(1);
    scrollToTop();
  };

  // Lancement de la recherche
  const handleSubmitSearch = (e) => {
    if (e) e.preventDefault();
    fetchUsers();
  };

  // Gestion du tri
  const handleSort = (field) => {
    if (field === sortField) {
      // Si on clique sur le même champ, on inverse l'ordre
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Sinon on trie par le nouveau champ en ordre descendant par défaut
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  // Fonction pour afficher l'indicateur de tri
  const getSortIndicator = (field) => {
    if (field !== sortField) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBirthDate = (dateString) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString || '');
    return match ? `${match[3]}/${match[2]}/${match[1]}` : 'Non renseignée';
  };

  // Fonction pour convertir un prénom en camelCase
  const toCamelCase = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        // Première lettre en majuscule, le reste en minuscule
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  // Construit la liste des pages à afficher avec ellipses
  // Exemple: 1, 2, 3, 4, 5, ..., 20
  // Exemple: 1, ..., 7, 8, 9, ..., 20
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    if (page <= 4) {
      pages.push(1, 2, 3, 4, 5, '...', totalPages);
      return pages;
    }
    if (page >= totalPages - 3) {
      pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      return pages;
    }
    pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    return pages;
  };

  return (
    <div ref={topRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Loader global non bloquant en haut de page */}
      <AdminLoader show={loading} message="Chargement des contacts…" />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold text-gray-900">Carnet d&apos;adresses</h2>
          <p className="mt-1 text-sm text-gray-500">Gérez les utilisateurs de la plateforme</p>
        </div>
        <button
          type="button"
          onClick={handleOpenCreateModal}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-[#0b8e36] text-white hover:bg-green-700 text-sm font-semibold"
          title="Créer un nouveau contact"
          aria-label="Créer un nouveau contact"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Nouveau contact
        </button>
      </div>
      {/* Affichage d'une alerte légère si erreur tout en gardant le contenu visible */}
      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      )}
      
        <div>
          {/* Statistiques compactes alignées sur le style des expéditions */}
          <div className="mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              {/* Carte principale: Contacts */}
              <div className="flex items-center gap-3 p-4 rounded-xl shadow-sm border border-orange-100 bg-orange-50">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-orange-600 shadow-inner">
                  {/* Icône utilisateurs */}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19c0 .35-.06.69-.18 1H23c.55 0 1-.45 1-1v-2.5C24 14.17 19.33 13 16 13z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wide text-orange-700 font-semibold">
                    {search && search.trim() ? 'Contacts trouvés' : 'Contacts totaux'}
                  </div>
                  <div className="text-2xl font-extrabold text-orange-800">
                    {search && search.trim() ? users.length : totalUsersCount}
                  </div>
                </div>
              </div>
              {/* Colonne droite: bouton de reset aligné verticalement */}
              <div className="flex sm:justify-end items-center">
                {search && search.trim() && (
                  <button
                    type="button"
                    title="Réinitialiser la recherche"
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      setSearch('');
                      setPage(1);
                      fetchUsers('');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                      <path d="M21 3v6h-6" />
                    </svg>
                    Réinitialiser
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Moteur de recherche et options */}
          <form onSubmit={handleSubmitSearch} className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(190px,auto)_minmax(120px,auto)] gap-3 md:gap-4 items-end mb-4 w-full">
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Recherche</label>
              <div className="flex w-full min-w-0">
                <input
                  type="text"
                  value={search}
                  onChange={handleSearch}
                  placeholder="Nom, email, téléphone, ville, pays..."
                  className="min-w-0 flex-1 border border-gray-300 !border-r-0 rounded-l-md rounded-r-none px-3 py-2 text-sm focus:ring-2 focus:ring-[#e88c00] focus:outline-none"
                />
                <button 
                  type="submit"
                  className="bg-[#e88c00] text-white px-4 py-2 rounded-l-none rounded-r-md hover:bg-orange-700 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Trier par</label>
              <select 
                value={`${sortField}-${sortOrder}`} 
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortField(field);
                  setSortOrder(order);
                  setPage(1);
                }}
                className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
              >
                <option value="lastname-asc">Nom (A-Z)</option>
                <option value="lastname-desc">Nom (Z-A)</option>
                <option value="firstname-asc">Prénom (A-Z)</option>
                <option value="firstname-desc">Prénom (Z-A)</option>
                <option value="created_at-desc">Date de création (récent)</option>
                <option value="created_at-asc">Date de création (ancien)</option>
              </select>
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Résultats/page</label>
              <select value={perPage} onChange={handlePerPage} className="w-full border border-gray-300 rounded px-2 py-2 text-sm">
                {[5, 10, 15, 20, 30, 50].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </form>
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="lg:hidden">
              {users.length === 0 && (
                <div className="px-6 py-4 text-center text-gray-500">Aucun utilisateur trouvé.</div>
              )}
              {paginatedUsers.map((user) => (
                <div key={user.id} className="border-t border-gray-200 p-4">
                  <div className="flex flex-col gap-2">
                    <div className="w-full flex items-start justify-between gap-3">
                      <div className="min-w-0 w-full">
                        <div className="font-semibold text-gray-900 whitespace-pre-line break-words w-full">
                          {user.lastname} {toCamelCase(user.firstname)}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {user.hasFamily ? (
                          <UserGroupIcon className="h-6 w-6 text-emerald-600" title="Avec membres de famille" />
                        ) : (
                          <UserIcon className="h-6 w-6 text-gray-400" title="Sans membres de famille" />
                        )}
                      </div>
                    </div>

                    <div className="w-full">
                      <div className="text-xs text-gray-500">Date de naissance</div>
                      <div className="text-sm font-medium text-gray-900">{formatBirthDate(user.birth_date)}</div>
                    </div>

                    <div className="w-full text-sm text-gray-900">
                      {user.email && (
                        <div className="flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                          <span className="break-words">{user.email?.toLowerCase()}</span>
                        </div>
                      )}
                      {user.phone && (
                        <div className="flex items-center gap-2 mt-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span className="break-words">{user.phone}</span>
                        </div>
                      )}
                    </div>

                    <div className="w-full">
                      <div className="text-xs text-gray-500">Adresse</div>
                      <div className="text-sm text-gray-900 whitespace-pre-line break-words w-full">
                        {user.address}{user.zipcode ? `\n${user.zipcode}` : ''}{user.city ? ` ${user.city}` : ''}{user.country ? `\n${user.country}` : ''}
                      </div>
                    </div>

                    <div className="w-full">
                      <div className="text-xs text-gray-500">Créé le</div>
                      <div className="text-sm text-gray-900 font-medium" title={user.created_at ? new Date(user.created_at).toISOString() : ''}>
                        {user.created_at ? formatDate(user.created_at) : '—'}
                      </div>
                    </div>

                    <div className="mt-2 flex flex-col gap-2">
                      <Link
                        to={`/admin/users/view/${user.id}`}
                        className="w-full px-3 py-2 bg-[#0b8e36] text-white rounded hover:bg-green-700 whitespace-nowrap flex items-center justify-center gap-2"
                        title="Voir les détails de l'utilisateur"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Voir
                      </Link>
                      <Link
                        to={`/admin/users/edit/${user.id}`}
                        className="w-full px-3 py-2 bg-[#e88c00] text-white rounded hover:bg-orange-700 whitespace-nowrap flex items-center justify-center gap-2"
                        title="Modifier l'utilisateur"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Modifier
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden lg:block w-full">
              <table className="w-full table-fixed divide-y divide-gray-200">
                <colgroup>
                  <col className="w-[15%]" />
                  <col className="w-[11%]" />
                  <col className="w-[22%]" />
                  <col className="w-[20%]" />
                  <col className="w-[14%]" />
                  <col className="w-[18%]" />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('lastname')}
                    >
                      Nom complet{getSortIndicator('lastname')}
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Naissance</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacts</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
                    <th 
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('created_at')}
                    >
                      Créé le{getSortIndicator('created_at')}
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500">Aucun utilisateur trouvé.</td>
                    </tr>
                  )}
                  {paginatedUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="px-3 py-4 whitespace-pre-line break-words text-sm text-gray-900 align-top">
                        <div className="flex items-start gap-2 min-w-0">
                          <span className="inline-flex h-5 w-5 items-center justify-center flex-none leading-none">
                            {user.hasFamily ? (
                              <UserGroupIcon className="h-5 w-5 text-emerald-600 flex-none" title="Avec membres de famille" />
                            ) : (
                              <UserIcon className="h-5 w-5 text-gray-400 flex-none" title="Sans membres de famille" />
                            )}
                          </span>
                          <span className="leading-tight min-w-0 break-words">{user.lastname} {toCamelCase(user.firstname)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900 align-top break-words">
                        {formatBirthDate(user.birth_date)}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900 align-top min-w-0">
                        {user.email && (
                          <div className="flex items-start gap-2 mb-1.5 min-w-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                            <span className="min-w-0 break-all leading-tight">{user.email?.toLowerCase()}</span>
                          </div>
                        )}
                        {user.phone && (
                          <div className="flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>{user.phone}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-pre-line break-words text-sm text-gray-900 align-top">{user.address}{user.zipcode ? ", " + user.zipcode : ""}{user.city ? ", " + user.city : ""}{user.country ? ", " + user.country : ""}</td>
                      <td className="px-3 py-4 text-sm text-gray-900 align-top break-words" title={user.created_at ? new Date(user.created_at).toISOString() : ''}>
                        {user.created_at ? formatDate(user.created_at) : ''}
                      </td>
                      <td className="px-3 py-4 text-sm font-medium align-top">
                        <div className="flex flex-col xl:flex-row gap-2">
                          <Link
                            to={`/admin/users/view/${user.id}`}
                            className="w-full px-2 py-1 bg-[#0b8e36] text-white rounded hover:bg-green-700 whitespace-nowrap flex items-center justify-center gap-1.5"
                            title="Voir les détails de l'utilisateur"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Voir
                          </Link>
                          <Link
                            to={`/admin/users/edit/${user.id}`}
                            className="w-full px-2 py-1 bg-[#e88c00] text-white rounded hover:bg-orange-700 whitespace-nowrap flex items-center justify-center gap-1.5"
                            title="Modifier l'utilisateur"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 min-w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Modifier
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination améliorée */}
            {totalPages > 1 && (
              <div className="flex flex-col items-center justify-center gap-3 mt-8 mb-12">
                {/* Info plage affichée */}
                <div className="text-xs text-gray-600 text-center">
                  Affichage <span className="font-semibold">{(page - 1) * perPage + 1}</span>
                  –<span className="font-semibold">{Math.min(page * perPage, users.length)}</span>
                  {' '}sur <span className="font-semibold">{users.length}</span>
                </div>
                {/* Contrôles de navigation */}
                <div className="flex flex-wrap items-center justify-center gap-1">
                  <button
                    className="px-2 py-1.5 rounded-md border text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => goToPage(1)}
                    disabled={page === 1}
                    aria-label="Première page"
                    title="Première page"
                  >
                    <span className="hidden sm:inline">«</span>
                    <span className="inline sm:hidden">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M11.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L7.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                        <path fillRule="evenodd" d="M16.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L12.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </button>
                  <button
                    className="px-2 py-1.5 rounded-md border text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => goToPage(page - 1)}
                    disabled={page === 1}
                    aria-label="Page précédente"
                    title="Page précédente"
                  >
                    <span className="hidden sm:inline">‹</span>
                    <span className="inline sm:hidden">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L8.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </button>
                  {getPageNumbers().map((p, idx) => (
                    typeof p === 'string' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-xs text-gray-500 select-none">…</span>
                    ) : (
                      <button
                        key={p}
                        className={
                          `px-3 py-1.5 rounded-md border text-xs font-semibold ` +
                          (p === page
                            ? 'bg-[#e88c00] text-white border-[#e88c00] cursor-default'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300')
                        }
                        onClick={() => goToPage(p)}
                        aria-current={p === page ? 'page' : undefined}
                        title={`Aller à la page ${p}`}
                      >
                        {p}
                      </button>
                    )
                  ))}
                  <button
                    className="px-2 py-1.5 rounded-md border text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => goToPage(page + 1)}
                    disabled={page === totalPages}
                    aria-label="Page suivante"
                    title="Page suivante"
                  >
                    <span className="hidden sm:inline">›</span>
                    <span className="inline sm:hidden">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L11.586 10 7.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </button>
                  <button
                    className="px-2 py-1.5 rounded-md border text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => goToPage(totalPages)}
                    disabled={page === totalPages}
                    aria-label="Dernière page"
                    title="Dernière page"
                  >
                    <span className="hidden sm:inline">»</span>
                    <span className="inline sm:hidden">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L12.586 10 8.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        <path fillRule="evenodd" d="M3.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L7.586 10 3.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      {/* Modale de création (style similaire à AdminHome) */}
      <Transition show={createModal.open} as={React.Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={handleCloseCreateModal}>
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>

            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-3xl overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-xl">
                <div className="flex justify-between items-center bg-[#0b8e36] p-4 text-white">
                  <Dialog.Title as="h3" className="text-xl font-bold">Créer un contact</Dialog.Title>
                  <button type="button" className="text-white hover:text-gray-200 transition-colors" onClick={handleCloseCreateModal}>
                    <span className="sr-only">Fermer</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="p-6">

                {createModal.error && (
                  <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{createModal.error}</div>
                )}

                <form onSubmit={handleCreateUser} className="space-y-4">
                  {/* Champ parent en premier */}
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <label className="block text-sm font-semibold text-blue-800 mb-2">Contact parent (optionnel)</label>
                    <input
                      list="parent-options"
                      value={form.parentInput}
                      onChange={handleParentInputChange}
                      onBlur={onParentInputBlur}
                      placeholder="Rechercher par nom, email, téléphone..."
                      className="w-full border border-blue-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <datalist id="parent-options">
                      {parentOptions.map(opt => (
                        <option key={opt.id} value={opt.label} />
                      ))}
                    </datalist>
                    {form.parent_id && (
                      <p className="mt-2 text-sm text-blue-700 font-medium">✓ Parent sélectionné - Création d&apos;un membre de famille</p>
                    )}
                    {!form.parent_id && (
                      <p className="mt-2 text-sm text-gray-600">Laissez vide pour créer un contact principal</p>
                    )}
                  </div>

                  {/* Champs conditionnels selon le type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Champs communs pour tous les types de contacts */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Nom *</label>
                      <input name="lastname" value={form.lastname} onChange={updateForm} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" required />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Prénom *</label>
                      <input name="firstname" value={form.firstname} onChange={updateForm} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" required />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Date de naissance *</label>
                      <input
                        type="date"
                        name="birth_date"
                        value={form.birth_date}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={updateForm}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        required
                      />
                    </div>
                    
                    {/* Email et téléphone - obligatoires pour users, optionnels pour family_members */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Email {isCreatingFamilyMember ? '(optionnel)' : '*'}
                      </label>
                      <input 
                        type="email" 
                        name="email" 
                        value={form.email} 
                        onChange={updateForm} 
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm" 
                        required={!isCreatingFamilyMember} 
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Téléphone {isCreatingFamilyMember ? '(optionnel)' : '*'}
                      </label>
                      <input 
                        name="phone" 
                        value={form.phone} 
                        onChange={updateForm} 
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm" 
                        required={!isCreatingFamilyMember} 
                      />
                    </div>
                    
                    {/* Champs uniquement pour les utilisateurs principaux (table users) */}
                    {!isCreatingFamilyMember && (
                      <>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Adresse *</label>
                          {/**
                            Champ d'adresse avec autocomplétion Google Places.
                            Remplit automatiquement adresse, ville, code postal et pays via `handlePlaceSelect`.
                            Les restrictions pays sont alignées sur `Payment.js` pour cohérence.
                          */}
                          <GooglePlacesAutocomplete
                            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                            onPlaceSelected={handlePlaceSelect}
                            options={{
                              types: ['address'],
                              componentRestrictions: { country: [
                                // France
                                'fr',
                                // Europe (UE)
                                'at','be','bg','hr','cy','cz','dk','ee','fi','de','gr','hu','ie','it','lv','lt','lu','mt','nl','pl','pt','ro','sk','si','es','se',
                                // DOM-TOM
                                'gf','gp','mq','yt','re','pm','bl','mf','wf','pf','nc','tf'
                              ] }
                            }}
                            placeholder="Saisissez l'adresse complète"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            // IMPORTANT: capturer la saisie manuelle pour alimenter form.address
                            // Sans cela, si l'utilisateur ne sélectionne pas une suggestion,
                            // form.address reste vide et la validation échoue.
                            name="address"
                            defaultValue={form.address}
                            onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Ville *</label>
                          <input name="city" value={form.city} onChange={updateForm} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" required />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Code postal *</label>
                          <input name="zipcode" value={form.zipcode} onChange={updateForm} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" required />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Pays *</label>
                          <input name="country" value={form.country} onChange={updateForm} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" required />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">Nom de l&apos;hôte (optionnel)</label>
                          <input name="host_name" value={form.host_name} onChange={updateForm} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="bg-gray-50 p-4 flex justify-end border-t border-gray-100 mt-4">
                    <button type="button" className="mr-2 inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500 transition-colors" onClick={handleCloseCreateModal}>
                      Annuler
                    </button>
                    <button type="submit" disabled={createModal.loading} className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0b8e36] transition-colors ${createModal.loading ? 'bg-[#0b8e36]/70' : 'bg-[#0b8e36] hover:bg-[#097a2e]'}`}>
                      {createModal.loading ? 'Création…' : (isCreatingFamilyMember ? 'Créer membre famille' : 'Créer contact')}
                    </button>
                  </div>
                </form>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default UsersList;
